import React from 'react'
import { SectionCard } from '../../components/sections/SectionCard'
import { Check, Star } from 'lucide-react'
import type { PricingTable, PricingTablePlan } from '@/features/subscriptions/types/pricing-table/pricing-table.types'

interface PlansSectionProps {
  isDetailView?: boolean
  pricingTableData?: PricingTable
  className?: string
}

function PlanCard({ plan }: { plan: PricingTablePlan }) {
  return (
    <div className="rounded-xl border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm">{plan.name}</span>
        {plan.highlighted && (
          <span className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">
            <Star className="size-3" />
            Highlighted
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1">
        {plan.price === 0 ? (
          <span className="text-xl font-bold">Custom</span>
        ) : (
          <>
            <span className="text-xl font-bold">
              {plan.currency === 'EUR' ? '€' : plan.currency}{plan.price}
            </span>
            <span className="text-xs text-muted-foreground">/{plan.interval}</span>
          </>
        )}
      </div>
      <div className="border-t pt-3">
        <ul className="space-y-1.5">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <Check className="size-3 mt-0.5 shrink-0 text-green-600" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export const PlansSection = React.memo<PlansSectionProps>(({
  isDetailView = false,
  pricingTableData,
  className,
}) => {
  const plans = pricingTableData?.plans || []

  if (plans.length === 0) {
    return (
      <SectionCard title="Plans" className={className}>
        <div className="py-8 text-center text-sm text-muted-foreground">
          No plans configured
        </div>
      </SectionCard>
    )
  }

  return (
    <SectionCard
      title={`Plans (${plans.length})`}
      isCollapsible={!isDetailView}
      className={className}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} />
        ))}
      </div>
    </SectionCard>
  )
})

PlansSection.displayName = 'PlansSection'
